import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { DragScrollComponent } from 'ngx-drag-scroll';
import { Subscription } from 'rxjs';
import {
  AvailabilityStatus,
  AvailabilityUpdateInfo,
  AvailableStatuses,
  EditionType,
  GetItRequest,
  HoldRequest,
  MaterialTab,
  VendorType,
} from '../../../../entity/models/entity';
import { ConfigurationLoader } from 'shared/configuration-loader';
import {
  checkEntityVendorAvailability,
  gatesLoadResourceCount,
  rollupTabSelected
} from '../../../../entity/actions/entity.actions';
import { RollUpTab } from '../../../../entity/models/rollup-card';
import { getSelectedRollupTab } from '../../../../entity/reducers/entity.reducer';
import { DictionaryItem, DictionaryTypes } from 'app/models/dictionaries';
import { DictionariesService } from 'app/services/dictionaries.service';
import {
  RollupTabsContentCardHeaderComponent
} from 'search/full-card/components/rollup-tabs-content-card-header/rollup-tabs-content-card-header.component';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { VendorIssuesService } from 'app/entity/services/vendor-issues.service';

@Component({
  selector: 'app-rollup-tabs-card-header',
  templateUrl: './rollup-tabs-card-header.component.html',
  styleUrls: ['./rollup-tabs-card-header.component.scss'],
})
export class RollupTabsCardHeaderComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() public fgId: string;
  @Input() public fgTitle: string;
  @Input() public materialTabs: MaterialTab[];
  @Input() public availabilityUpdates: AvailabilityUpdateInfo[];
  @Input() public language: string;
  @Output() public getIt$ = new EventEmitter<{ item: GetItRequest }>(true);
  @Output() public placeHold$ = new EventEmitter<{ item: HoldRequest }>(true);
  @Output() public tableInited = new EventEmitter();

  @ViewChild('nav', {read: DragScrollComponent}) public dragScroll: DragScrollComponent;
  @ViewChildren('rollupTabsContentCardHeaderComponent') private rollupTabs: QueryList<RollupTabsContentCardHeaderComponent>;

  public availabilityStatuses = AvailabilityStatus;
  public availableStatuses = AvailableStatuses;
  public activeId: number;
  public subscriptions = new Subscription();
  private activeIdChanged = false;
  public formatByCode: DictionaryItem;
  public readonly arrowIconRight = faAngleRight;
  public readonly arrowIconLeft = faAngleLeft;

  public hasEcontentIssues: boolean[];

  public constructor(
    public readonly store: Store,
    private configLoader: ConfigurationLoader,
    public elementRef: ElementRef,
    private readonly dictionariesService: DictionariesService,
    private readonly vendorIssuesService: VendorIssuesService
  ) { }

  public moveLeft() {
    this.dragScroll.moveLeft();
  }

  public moveRight() {
    this.dragScroll.moveRight();
  }

  public showArrowButton() {
    const dragScrollContent = this.elementRef.nativeElement.querySelector('.drag-scroll-content');
    return dragScrollContent ? dragScrollContent.scrollWidth > dragScrollContent.clientWidth : false;
  }

  public isAlwaysAvailableType(tab: MaterialTab) {
    this.formatByCode = this.dictionariesService.getDictionaryItemByCode(
      DictionaryTypes.MATERIAL_TYPES, tab?.materialTypes?.[0]
    );
    return this.formatByCode?.alwaysAvailable;
  }

  public ngOnInit(): void {
    this.selectRollupTab();
    this.materialTabs.forEach((el: MaterialTab) => {
      this.isAlwaysAvailableType(el);
    });

    this.hasEcontentIssues = this.materialTabs?.map(tab => {
      return this.vendorIssuesService.hasVendorIssuesForTab(tab);
    });
  }

  public ngAfterViewInit() {
    if (this.activeIdChanged) {
      this.dragScroll.moveTo(this.activeId);
      this.activeIdChanged = false;
    }

    // if material tab is set to active, set the active tab on the UI
    if (this.materialTabs && this.materialTabs.length) {
      const index = this.materialTabs.findIndex(p => p.active);
      if (index > -1) {
        const tabEvent = {
          nextId: index,
        } as NgbNavChangeEvent;
        this.onTabChange(tabEvent);
      }
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.materialTabs?.currentValue?.length) {
      this.selectRollupTab();
    }
  }

  public hasOverdrive(tab: MaterialTab) {
    const connections = this.configLoader.connectionsConfiguration;
    const hasOverdriveVendor = tab.availability.vendors?.overdrive;
    // if overdriveMagazineEnabled dont request availability
    return connections
      && connections.includes(VendorType.OVERDRIVE)
      && hasOverdriveVendor
      && !this.vendorIssuesService.hasVendorIssuesForTab(tab);
  }

  public hasAxis360(tab: MaterialTab) {
    const connections = this.configLoader.connectionsConfiguration;
    const hasAxis360Vendor = tab.availability.vendors?.axis360;
    return connections && connections.includes(VendorType.AXIS360) && hasAxis360Vendor;
  }

  public selectRollupTab() {
    this.subscriptions.add(this.store.select(getSelectedRollupTab(this.fgId)).subscribe((selectedTab) => {
      let selectedTabIndex = selectedTab && selectedTab.selectedIndex;
      this.activeIdChanged = this.activeId !== selectedTabIndex;
      this.activeId = selectedTabIndex || 0;
      if (!selectedTabIndex && selectedTabIndex !== 0) {
        selectedTabIndex = this.availabilityUpdates?.findIndex(
          (info: AvailabilityUpdateInfo) => info.status?.general === AvailabilityStatus.AVAILABLE);
        if (selectedTabIndex >= 0) {
          const payload = {
            resourceId: this.fgId,
            tab: this.activeId,
          };
          this.store.dispatch(rollupTabSelected(payload));
        }
      }
    }));
  }

  public onTabChange(event: NgbNavChangeEvent) {
    const tab: MaterialTab = this.materialTabs[event.nextId];
    const tabInfo: RollUpTab = {
      resourceId: this.fgId,
      tab: event.nextId,
    };
    this.store.dispatch(rollupTabSelected(tabInfo));
    this.dragScroll.moveTo(event.nextId);

    if (tab?.type === EditionType.ELECTRONIC) {
      if ((event.nextId !== this.materialTabs.length) && this.hasOverdrive(tab)) {
        this.store.dispatch(checkEntityVendorAvailability({
          vendor: VendorType.OVERDRIVE,
          content: {
            resourceId: this.fgId,
            reserveId: tab.availability.vendors.overdrive.reserveId,
          },
        }));
      } else if ((event.nextId !== this.materialTabs.length) && this.hasAxis360(tab)) {
        this.store.dispatch(checkEntityVendorAvailability({
          vendor: VendorType.AXIS360,
          content: {
            resourceId: this.fgId,
            reserveId: tab.availability.vendors.axis360.reserveId,
          },
        }));
      }

    } else if (tab) {
      this.store.dispatch(gatesLoadResourceCount({recordIds: tab.recordIds}));
    }
  }

  public trackById(index: number, item: MaterialTab) {
    return item.name;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onTableInited() {
    this.tableInited.emit();
  }
}
